export const GLOBAL_KEY = 'jw.nightMode';

export interface NightSnapshot {
    night: boolean;
    globalOn: boolean;
    canvasReady: boolean;
    pathname: string;
}

type Listener = () => void;

const listeners = new Set<Listener>();

function readStorage(): Storage | null {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function effectiveNight(pathname: string, storage: Storage | null = readStorage()): boolean {
    if (pathname === '/jail' || pathname.startsWith('/jail/')) return true;
    return storage?.getItem(GLOBAL_KEY) === 'on';
}

function isNightReady(): boolean {
    // Every page uses the night palette. light-canvas remains an opt-out.
    return true;
}

let pathname = typeof window !== 'undefined' ? window.location.pathname : '/';

function compute(): NightSnapshot {
    const night = effectiveNight(pathname);
    return {
        night,
        globalOn: readStorage()?.getItem(GLOBAL_KEY) === 'on',
        canvasReady: night && !isNightReady(),
        pathname,
    };
}

let snapshot: NightSnapshot = compute();

function publish() {
    snapshot = compute();
    listeners.forEach((listener) => listener());
}

export function getNightSnapshot(): NightSnapshot {
    return snapshot;
}

export function subscribeNight(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function syncNightMode(path: string): void {
    pathname = path;
    const night = effectiveNight(path);
    if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', night);
        document.documentElement.style.colorScheme = night ? 'dark' : 'light';
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute('content', night ? '#1A1A1A' : '#F5F5F5');
    }
    publish();
}

export function setGlobalNight(on: boolean): void {
    const storage = readStorage();
    if (!storage) return;
    if (on) storage.setItem(GLOBAL_KEY, 'on');
    else storage.removeItem(GLOBAL_KEY);
    syncNightMode(pathname);
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key === GLOBAL_KEY) syncNightMode(pathname);
    });
}
